import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Search, Download, AlertTriangle, Building2, Crown } from "lucide-react";

const formatBR = (v) => `R$ ${(Math.abs(Number(v) || 0)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const BalancesByPersonView = () => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyDebtors, setOnlyDebtors] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/balances-by-person?only_debtors=${onlyDebtors}`);
      setRows(res.data || []);
    } catch { toast.error("Erro ao carregar saldos."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyDebtors]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filterKind !== "all") {
      if (filterKind === "irmao" && r.kind !== "irmao") return false;
      if (filterKind === "contato" && r.kind !== "contato") return false;
      if (filterKind === "over90" && (r.over90 || 0) <= 0) return false;
    }
    if (searchTerm.trim()) {
      const s = searchTerm.trim().toLowerCase();
      const normalizedCimSearch = s.replace(/^cim\s*/i, "").replace(/\D/g, "");
      const normalizedCim = String(r.cim || "").replace(/\D/g, "");
      return r.name?.toLowerCase().includes(s)
        || (normalizedCimSearch && normalizedCim.includes(normalizedCimSearch))
        || r.email?.toLowerCase().includes(s);
    }
    return true;
  }), [rows, filterKind, searchTerm]);

  const totals = useMemo(() => {
    const t = { count: filtered.length, total: 0, current: 0, d30: 0, d60: 0, d90: 0, over90: 0 };
    filtered.forEach((r) => {
      t.total += r.total_open || 0;
      t.current += r.current || 0;
      t.d30 += r.d30 || 0;
      t.d60 += r.d60 || 0;
      t.d90 += r.d90 || 0;
      t.over90 += r.over90 || 0;
    });
    return t;
  }, [filtered]);

  const exportCSV = () => {
    const header = "Tipo;Nome;CIM/ID;Total;Em Dia;1-30 dias;31-60 dias;61-90 dias;90+ dias;Lançamentos\n";
    const lines = filtered.map((r) =>
      [
        r.kind === "irmao" ? "Irmão" : (r.contact_kind || "Contato"),
        (r.name || "").replace(/;/g, ","),
        r.cim || r.id || "",
        (r.total_open || 0).toFixed(2),
        (r.current || 0).toFixed(2),
        (r.d30 || 0).toFixed(2),
        (r.d60 || 0).toFixed(2),
        (r.d90 || 0).toFixed(2),
        (r.over90 || 0).toFixed(2),
        r.count || 0,
      ].join(";")
    ).join("\n");
    const blob = new Blob(["\uFEFF" + header + lines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Saldo_Por_Pessoa_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exportado.");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-lg text-white flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            Saldo por Pessoa &amp; Aging de Recebíveis
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Lista Irmãos e Contatos com valores em aberto, organizados por tempo de atraso.
          </p>
        </div>
        <button
          data-testid="btn-export-balances-csv"
          onClick={exportCSV}
          className="flex items-center gap-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-[#D4AF37]/40 font-semibold px-3 py-2 rounded-xl text-xs"
        >
          <Download className="w-3.5 h-3.5 text-[#D4AF37]" /> CSV
        </button>
      </div>

      {/* Cards de totais por faixa */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <div data-testid="balances-total-current" className="p-3 bg-[#080C14] rounded-xl border border-emerald-500/30">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Em Dia</div>
          <div className="font-mono text-lg text-emerald-300 font-bold">{formatBR(totals.current)}</div>
        </div>
        <div data-testid="balances-total-30d" className="p-3 bg-[#080C14] rounded-xl border border-yellow-500/30">
          <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold">1-30 dias</div>
          <div className="font-mono text-lg text-yellow-300 font-bold">{formatBR(totals.d30)}</div>
        </div>
        <div data-testid="balances-total-60d" className="p-3 bg-[#080C14] rounded-xl border border-orange-500/30">
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">31-60 dias</div>
          <div className="font-mono text-lg text-orange-300 font-bold">{formatBR(totals.d60)}</div>
        </div>
        <div data-testid="balances-total-90d" className="p-3 bg-[#080C14] rounded-xl border border-rose-500/30">
          <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold">61-90 dias</div>
          <div className="font-mono text-lg text-rose-300 font-bold">{formatBR(totals.d90)}</div>
        </div>
        <div data-testid="balances-total-over90" className="p-3 bg-[#080C14] rounded-xl border border-rose-700/50">
          <div className="text-[10px] uppercase tracking-widest text-rose-500 font-bold flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> 90+ dias
          </div>
          <div className="font-mono text-lg text-rose-400 font-bold">{formatBR(totals.over90)}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="balances-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome, CIM ou e-mail..."
            className="w-full pl-9 pr-3 py-2 bg-[#080C14] text-white text-xs rounded-lg border border-slate-800 focus:border-[#D4AF37]"
          />
        </div>
        {[
          { id: "all", label: `Todos (${rows.length})` },
          { id: "irmao", label: `Irmãos` },
          { id: "contato", label: `Contatos` },
          { id: "over90", label: `⚠ 90+ dias`, cls: "border-rose-500/50 text-rose-300" },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`btn-filter-balance-${f.id}`}
            onClick={() => setFilterKind(f.id)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider border ${
              filterKind === f.id
                ? "bg-[#D4AF37] text-slate-950 border-[#D4AF37]"
                : (f.cls || "bg-[#131C2E] text-slate-400 border-slate-700 hover:text-white")
            }`}
          >{f.label}</button>
        ))}
        <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer ml-2">
          <input data-testid="balances-only-debtors-checkbox" type="checkbox" checked={onlyDebtors} onChange={(e) => setOnlyDebtors(e.target.checked)} className="accent-[#D4AF37]" />
          Somente com saldo aberto
        </label>
      </div>

      {/* Tabela */}
      {loading ? (
        <div data-testid="balances-loading" className="p-8 text-center text-slate-500 text-sm italic">Carregando saldos...</div>
      ) : filtered.length === 0 ? (
        <div data-testid="balances-empty" className="p-8 text-center text-slate-500 text-sm italic">Nenhum registro corresponde aos filtros.</div>
      ) : (
        <div className="luxury-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="balances-table" className="w-full text-xs">
              <thead className="bg-[#080C14]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-800">
                  <th className="py-3 px-3">Tipo</th>
                  <th className="py-3 px-3">Nome</th>
                  <th className="py-3 px-3">CIM/ID</th>
                  <th className="py-3 px-3 text-right">Total Aberto</th>
                  <th className="py-3 px-3 text-right">Em Dia</th>
                  <th className="py-3 px-3 text-right">1-30d</th>
                  <th className="py-3 px-3 text-right">31-60d</th>
                  <th className="py-3 px-3 text-right">61-90d</th>
                  <th className="py-3 px-3 text-right">90+ dias</th>
                  <th className="py-3 px-3 text-center">Lanç.</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const isNeg = (r.total_open || 0) < -0.01;
                  const isCredit = r.balance_nature === "credito" || isNeg;
                  const isDebit = r.balance_nature === "debito";
                  const isZero = Math.abs(r.total_open || 0) <= 0.01;
                  const totalColor = isCredit ? "text-emerald-400" : isDebit ? "text-rose-400" : isZero ? "text-slate-500" : "text-[#D4AF37]";
                  const rowBg = isDebit ? "bg-rose-950/10" : (r.over90 || 0) > 0 ? "bg-rose-950/20" : "";
                  return (
                    <tr
                      key={r.kind + "_" + r.id + "_" + i}
                      data-testid={`row-balance-${r.id}`}
                      className={`border-b border-slate-900 hover:bg-white/5 ${rowBg}`}
                    >
                      <td className="py-2.5 px-3">
                        {r.kind === "irmao" ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-[#D4AF37]">
                            <Crown className="w-3 h-3" />Irmão
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-cyan-400">
                            <Building2 className="w-3 h-3" />{r.contact_kind || "Contato"}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-white">
                        {r.name}
                        {isCredit && <span data-testid={`balance-credit-badge-${r.id}`} title="Crédito em favor da pessoa" className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">CRÉDITO</span>}
                        {isDebit && <span data-testid={`balance-debit-badge-${r.id}`} title="Débito pendente da Loja" className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40">DÉBITO</span>}
                        {isZero && <span title="Em dia" className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">EM DIA</span>}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-400">{r.cim || r.id?.substring(0, 12)}</td>
                      <td data-testid={`balance-total-${r.id}`} className={`py-2.5 px-3 text-right font-mono font-bold ${totalColor}`}>
                        {formatBR(Math.abs(r.total_open))}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-emerald-400">{(r.current || 0) > 0 ? formatBR(r.current) : "—"}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-yellow-400">{(r.d30 || 0) > 0 ? formatBR(r.d30) : "—"}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-orange-400">{(r.d60 || 0) > 0 ? formatBR(r.d60) : "—"}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-rose-400">{(r.d90 || 0) > 0 ? formatBR(r.d90) : "—"}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-rose-500 font-bold">{(r.over90 || 0) > 0 ? formatBR(r.over90) : "—"}</td>
                      <td className="py-2.5 px-3 text-center text-slate-400">{r.count}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-[#080C14] border-t-2 border-[#D4AF37]/50">
                <tr className="text-xs font-bold">
                  <td colSpan={3} className="py-3 px-3 text-slate-400 uppercase tracking-widest">Totais ({totals.count})</td>
                  <td className="py-3 px-3 text-right font-mono text-[#D4AF37]">{formatBR(totals.total)}</td>
                  <td className="py-3 px-3 text-right font-mono text-emerald-400">{formatBR(totals.current)}</td>
                  <td className="py-3 px-3 text-right font-mono text-yellow-400">{formatBR(totals.d30)}</td>
                  <td className="py-3 px-3 text-right font-mono text-orange-400">{formatBR(totals.d60)}</td>
                  <td className="py-3 px-3 text-right font-mono text-rose-400">{formatBR(totals.d90)}</td>
                  <td className="py-3 px-3 text-right font-mono text-rose-500">{formatBR(totals.over90)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
